import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon } from 'lucide-react';
interface ExecutiveSummaryProps {
  open: boolean;
  onClose: () => void;
}
export function ExecutiveSummary({
  open,
  onClose
}: ExecutiveSummaryProps) {
  return <AnimatePresence data-id="element-28">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-29">
          <motion.div initial={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-30">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-200 bg-white sticky top-0 z-10" data-id="element-31">
              <div className="flex items-center gap-3" data-id="element-32">
                <div className="w-10 h-10 rounded-xl bg-[#6C2BD9] flex items-center justify-center" data-id="element-33">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-34" />
                </div>
                <div data-id="element-35">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#6C2BD9] font-semibold" data-id="element-36">
                    Snakker · Investor Pitch
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-id="element-37">
                    Executive Summary
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-38">
                <XIcon className="w-5 h-5 text-slate-600" data-id="element-39" />
              </button>
            </div>

            <article className="overflow-y-auto px-6 md:px-12 py-10 prose prose-slate max-w-none" data-id="element-40">
              <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-8" data-id="element-41">
                Snakker is the AI-native engine for{' '}
                <span className="text-[#6C2BD9]" data-id="element-42">
                  Legacy Brand Optimization
                </span>{' '}
                — unlocking value from pharma's revenue tail through continuous,
                scalable optimization of mature and off-patent brands.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-43">
                The Pharma Lifecycle
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-44">
                Every pharma product moves through four phases.{' '}
                <strong data-id="element-45">R&D</strong> runs deep into the negative through
                discovery and clinical development. <strong data-id="element-46">Launch</strong> is
                the rapid rise as the brand reaches market and starts generating
                revenue. <strong data-id="element-47">Mature</strong> is the peak — strong share,
                engaged prescribers, and the cash that funds the rest of the
                portfolio. Then comes <strong data-id="element-48">Legacy</strong>: patents expire,
                attention shifts to newer assets, marketing investment is
                pulled, and revenue typically falls off a cliff and flattens
                into a thin tail just above zero. Snakker focuses entirely on
                this Legacy phase — the strategic move is to{' '}
                <em data-id="element-49">soften the cliff and extend the tail</em>, where small
                improvements compound into enormous portfolio value.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-50">
                The Market
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-51">
                Roughly <strong data-id="element-52">30–35% of big pharma revenue</strong> comes from
                mature and legacy brands, and{' '}
                <strong data-id="element-53">$180–300B+ in revenue</strong> is facing loss of
                exclusivity in the coming years. This is the part of the
                portfolio that quietly funds the next generation of innovation —
                and it is the most under-optimized segment in the industry.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-54">
                The Problem
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-55">
                When patents expire, generic competition can cause sales to drop
                more than 90% almost overnight for small molecules — and
                marketing investment is stopped immediately. Sales teams are
                reassigned, budgets cut, and when the gap between revenue and
                cost approaches zero the product is taken off the market. The
                real problem isn't that these brands lack value — it's that{' '}
                <strong data-id="element-56">traditional marketing is simply too expensive</strong>{' '}
                to justify on declining revenue, so brands die earlier than they
                need to. Biologics face the same end state, just on a slower
                curve.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-57">
                The Business Model
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-58">
                Snakker takes over the annual <strong data-id="element-59">campaign calendar</strong>{' '}
                for portfolios of legacy brands across multiple countries —
                typically <strong data-id="element-60">3–4 campaigns per product per year</strong>.
                One contract per account, one operating partner, predictable
                annual revenue. Four accounts are already running this model:{' '}
                <strong data-id="element-61">GSK</strong> (Early Stage III — 5 products in Nigeria &
                Kenya, expanding to 7 Gulf countries),{' '}
                <strong data-id="element-62">Solventum</strong> (Late Stage II — MedSurge + Filltek
                divisions across UK, Germany, Nordics & South Africa),{' '}
                <strong data-id="element-63">Leo Pharma</strong> (Stage II — 5 products across UK,
                Brazil, Poland & Greece) and{' '}
                <strong data-id="element-64">Roche Pharmaceutical</strong> (Early Stage II — 3
                products across China, Canada, Italy & Germany). Each one
                represents a potential of <strong data-id="element-65">100+ countries</strong> and{' '}
                <strong data-id="element-66">10× the current brand count</strong>.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-67">
                The Unlocked Value
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-68">
                Even a <strong data-id="element-69">1% improvement</strong> across a legacy portfolio
                represents tens of millions of euros per year. Modeled against a
                pharma division of €5–60B with 35% legacy revenue, the
                unmanaged, under-optimized value sitting on the table is
                enormous — and it compounds every quarter it's left alone.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-70">
                Why AI Alone Isn't Enough
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-71">
                Generative AI dramatically reduces the cost of content creation,
                making it theoretically possible to support the long tail. But
                AI alone is not enough.{' '}
                <strong data-id="element-72">Execution is the bottleneck</strong> — what actually
                moves legacy revenue is strategy, compliance, distribution and
                measurement working together as a system.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-73">
                What Makes Us Structurally Different
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-74">
                Backed by an <strong data-id="element-75">Innobooster grant</strong>, we have turned
                thirty years of pharma campaign models into AI workflows.
                Storyboarding and creation now happen in minutes, not months,
                producing short, engaging, TA-relevant campaigns ready for
                deployment. Four things set Snakker apart:{' '}
                <strong data-id="element-76">deep vertical expertise and master agreements</strong>{' '}
                in regulated environments where generalist AI fails; a{' '}
                <strong data-id="element-77">one-to-one precision delivery model</strong> versus
                agencies' broad-stroke campaigns; a{' '}
                <strong data-id="element-78">proprietary interactive engagement platform</strong>{' '}
                that drives measurable outcomes; and a{' '}
                <strong data-id="element-79">compounding behavioural flywheel</strong> in which data
                refines the model weekly, widening the gap over time.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-80">
                A New Category: Campaign Service Management
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-81">
                Snakker is not an agency and not a SaaS tool. We are creating a
                new category — <strong data-id="element-82">Campaign Service Management</strong> —{' '}
                managed pharma campaign execution powered by software economics.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-83">
                Three Levers
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-84">
                Our approach pulls three levers that compound on each other. We{' '}
                <strong data-id="element-85">reduce erosion</strong> through continuous, data-driven
                engagement that keeps HCPs connected to the brand. We{' '}
                <strong data-id="element-86">increase profitability</strong> by replacing traditional
                marketing with AI-automated execution, widening the margin on
                every euro of legacy revenue. And we{' '}
                <strong data-id="element-87">prolong lifespan</strong> — lower costs and slower
                erosion keep the gap between revenue and cost positive for
                longer, so products stay on market generating value instead of
                being withdrawn prematurely.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-88">
                The Operating Model
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-89">
                Snakker runs on four pillars. We{' '}
                <strong data-id="element-90">leverage existing assets</strong> — no new content
                creation required, we repurpose approved materials into
                interactive formats. We achieve{' '}
                <strong data-id="element-91">rapid deployment</strong> through centralized creation
                with localized distribution. We perform{' '}
                <strong data-id="element-92">behavioral data capture</strong> that moves beyond
                vanity metrics to reveal what actually drives HCP decisions. And
                we run a <strong data-id="element-93">continuous optimization</strong> loop where
                every interaction informs the next campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-94">
                What Snakker Does
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-95">
                Snakker acts as the operating layer between strategy and
                execution. We create campaigns from approved content, turn them
                into short interactive <strong data-id="element-96">Snaks™</strong>, publish them
                across channels, capture behavioral data on every interaction,
                and improve campaign after campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-97">
                Snaks Throughout the Customer Journey
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-98">
                The same Snak format adapts to every stage of the HCP journey:{' '}
                <strong data-id="element-99">Awareness</strong> (condition framing, diagnostic
                prompts), <strong data-id="element-100">Education</strong> (disease state, mechanism
                of action, preference exploration), <strong data-id="element-101">Engagement</strong>{' '}
                (live polls, peer benchmarks, speciality routing), and{' '}
                <strong data-id="element-102">Conversion & Loyalty</strong> (treatment journeys,
                role-based content, congress follow-up). Every Snak captures
                behavioural data that sharpens the next campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-103">
                Platform Architecture
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-104">
                Behind the product runs an AI-driven closed-loop architecture
                organized as Think · Create · Learn · Improve, built on three
                layers: the <strong data-id="element-105">AI Intelligence Engine</strong> (blueprint
                analysis, story generation, content creation, channel
                optimization), the <strong data-id="element-106">Data Supply Chain</strong>{' '}
                (therapeutic-area themes, brand data, HCP insights, market
                intelligence), and the <strong data-id="element-107">Application Suite</strong> (Snak
                Composer, Snakker Player, Distribution, Analytics).
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-108">
                The Moat
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-109">
                Defensibility comes from four compounding assets:{' '}
                <strong data-id="element-110">medical storytelling</strong> as codified pharma
                expertise, a{' '}
                <strong data-id="element-111">short-format interactive engagement platform</strong>{' '}
                driving measurable outcomes, deep{' '}
                <strong data-id="element-112">pharma distribution</strong> relationships, and a
                privacy-safe <strong data-id="element-113">behavioural dataset</strong> that compounds
                performance with every campaign. Together they form an
                Unbreakable Flywheel that no broad player can match inside this
                regulated vertical.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-114">
                Why We're Raising
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-115">
                Out of necessity,{' '}
                <strong data-id="element-116">
                  85% of our sales effort today goes to Stage I pilots
                </strong>
                . They close in 4–7 weeks and bring in cash, but cap at €5–10K —
                leaving the larger Stage II (€25–60K, 3–6 months), Stage III
                (€100K+, 4–8 months) and Stage IV (€1M+, 12+ months)
                opportunities sitting idle. Today's pipeline already includes{' '}
                <strong data-id="element-117">9 named accounts in Stage I</strong> (Nestlé HS, CSL
                Vifor, Bayer, Abbott, AstraZeneca, MSD, Sanofi, Novartis,
                Boehringer), <strong data-id="element-118">4 in Stage II</strong> (Solventum,
                Menarini, Roche, Leo Pharma), and{' '}
                <strong data-id="element-119">GSK in early Stage III</strong>. Investment buys the{' '}
                <strong data-id="element-120">liquidity to run longer sales cycles</strong> and shift
                focus to <strong data-id="element-121">10% / 20% / 30% / 40%</strong> across Stages I
                → IV. The plan is to{' '}
                <strong data-id="element-122">
                  move existing Stage I and II clients into Stage III, and Stage
                  III into Stage IV
                </strong>{' '}
                — same accounts, 10× the deal size.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-123">
                Fast Marketing™ Revenue per Account
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-124">
                ACV per pharma account is built on three list-price units that
                discount with volume: a{' '}
                <strong data-id="element-125">Master Snak from €2,900 down to €1,900</strong> as
                campaign volume scales, country localization{' '}
                <strong data-id="element-126">from €999 down to €499</strong> (0% → 50% off as country
                count grows from 2 → 50+), and{' '}
                <strong data-id="element-127">
                  data insights & hosting from €99/month per country
                </strong>{' '}
                with the same volume curve. Versus a traditional digital pharma
                campaign — banners, emails, WhatsApp GIFs plus strategy,
                creative, regulatory review and local adaptation — which costs{' '}
                <strong data-id="element-128">€15,000–€19,000 per campaign per country</strong>, the
                Snakker model lands a fully managed, localized deployment in the{' '}
                <strong data-id="element-129">low thousands</strong>. ACV scales predictably as each
                account adds brands, campaigns and countries.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-130">
                Financial Projections — 2026–2029
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-131">
                Revenue scales{' '}
                <strong data-id="element-132">DKK 5.82M → 12.82M → 22.75M → 36M</strong> across
                2026–2029. The business turns{' '}
                <strong data-id="element-133">profitable in 2028</strong> with a net result of{' '}
                <strong data-id="element-134">+DKK 5.45M</strong>, growing to{' '}
                <strong data-id="element-135">+DKK 18.6M in 2029</strong>. 2026 and 2027 stay
                investment-mode (net results of −DKK 1.39M and −DKK 1.51M) as we
                fund the move into Stage II–IV deals.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-136">
                The Raise
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-137">
                We are raising <strong data-id="element-138">DKK 4M</strong>, allocated{' '}
                <strong data-id="element-139">60% to Account Management</strong> (the sales capacity
                needed to push Stage II clients into Stage III and Stage III
                into Stage IV), <strong data-id="element-140">20% to AI Optimization</strong> and{' '}
                <strong data-id="element-141">20% to Delivery Capability</strong>. The outcome is a
                scalable AI-native business with compounding learning dynamics
                and recurring runtime revenue.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-142">
                In One Line
              </h3>
              <p className="text-lg font-semibold text-slate-900 leading-snug mt-2 pb-4" data-id="element-143">
                Snakker is Campaign Service Management for legacy pharma — and
                this raise is what moves our 14 named pharma accounts from
                pilots into the campaign-calendar model running today inside
                GSK, Solventum, Leo Pharma and Roche.
              </p>
            </article>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}